import polylabel from "polylabel";
import { area, bbox, booleanIntersects, polygon } from "@turf/turf";
import { feature, featureCollection } from "@turf/helpers";
import { searchSingleLayerForConflicts } from "src/components/ConflictSearchCalculations";
import { DataCollector } from "src/dataCollector/DataCollector";
import { AddressInterface } from "src/managers/AddressInterface";
import { GenerateGeom } from "src/managers/GenerateGeom";
import { MapitUtils } from "src/managers/MapitUtils";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import type { LayerInfo } from "src/common/managers/Types";

export type JpbSubjectInfoResult = {
  subjectAddress?: string;
  subjectKommune?: string;
  subjectBfe?: string;
  subjectMatr?: string;
};

type LngLat = { lng: number; lat: number };

const BFE_IN_LABEL_RE = /\((\d{5,})\)/;
const MULTI_VALUE_PLACEHOLDER = "m.fl.";
const MATRIKEL_CONFLICT_LAYER = "Jordstykke_Gaeldende" as const;

type JordstykkeOverlapInfo = {
  identity: string;
  matnr: string;
  ejerlav: string;
  ejerlavskode: string;
  bfe: string;
};

function collectLngLatsFromPointLayer(layer: LayerInfo): [number, number][] {
  const out: [number, number][] = [];
  const gj = layer.geoJson;
  if (!gj?.features?.length) {
    return out;
  }
  for (const f of gj.features) {
    const g = f.geometry as { type: string; coordinates?: unknown } | undefined;
    if (!g) {
      continue;
    }
    if (g.type === "Point" && Array.isArray(g.coordinates)) {
      const c = g.coordinates as number[];
      if (c.length >= 2) {
        out.push([c[0], c[1]]);
      }
    } else if (g.type === "MultiPoint" && Array.isArray(g.coordinates)) {
      for (const c of g.coordinates as number[][]) {
        if (c && c.length >= 2) {
          out.push([c[0], c[1]]);
        }
      }
    }
  }
  return out;
}

function readBfeFromProperties(properties: Record<string, unknown> | undefined): string | undefined {
  if (!properties) {
    return undefined;
  }
  const raw =
    properties.BFEnummer ??
    properties.SFEnummer ??
    properties.samletfastejendomlokalid ??
    properties.samletFastEjendomLokalId ??
    properties.bfe ??
    properties.BFEnr ??
    properties.bfenr;
  if (raw === undefined || raw === null || raw === "") {
    return undefined;
  }
  return String(raw).trim();
}

export function extractBfeFromLayer(layer: LayerInfo | undefined): string | undefined {
  if (!layer) {
    return undefined;
  }
  for (const f of layer.geoJson?.features ?? []) {
    const fromProps = readBfeFromProperties(f?.properties as Record<string, unknown> | undefined);
    if (fromProps) {
      return fromProps;
    }
  }
  const label =
    layer.datasetname ??
    (layer as LayerInfo & { layer?: { label?: string; name?: string } }).layer?.label ??
    (layer as LayerInfo & { layer?: { label?: string; name?: string } }).layer?.name ??
    "";
  const match = label.match(BFE_IN_LABEL_RE);
  return match?.[1];
}

export function isBfePropertyPolygonLayer(layer: LayerInfo | undefined): boolean {
  if (!layer || MapitUtils.isPointLayer(layer.type)) {
    return false;
  }
  if (extractBfeFromLayer(layer)) {
    return true;
  }
  const keys = layer.propertiesInGeoJson ?? [];
  return keys.some((key) => /sfe|bfe|samletfastejendom/i.test(key));
}

/** User-drawn / custom polygon layers — not a pin-expanded SFE or cadaster BFE layer. */
export function isArbitraryPolygonAnalysis(layer: LayerInfo | undefined): boolean {
  if (!layer) {
    return true;
  }
  if (MapitUtils.isPointLayer(layer.type)) {
    return false;
  }
  return !isBfePropertyPolygonLayer(layer);
}

function asPolygonGeometry(
  geometry: { type?: string; coordinates?: unknown } | null | undefined,
): GeoJSON.Polygon | GeoJSON.MultiPolygon | null {
  if (
    !geometry?.type ||
    !geometry.coordinates ||
    (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")
  ) {
    return null;
  }
  return geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

function filterFeaturesIntersectingGeometry(
  features: GeoJSON.Feature[],
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
): GeoJSON.Feature[] {
  const searchFeature = feature(geometry);
  return features.filter((f) => {
    if (!f.geometry) {
      return false;
    }
    return booleanIntersects(
      { type: "Feature", geometry: f.geometry, properties: f.properties ?? {} },
      searchFeature,
    );
  });
}

async function searchOverlappingWfsFeatures(
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
  layer: string,
): Promise<GeoJSON.Feature[]> {
  const searchGeom = featureCollection([feature(geometry)]);
  const seen = new Set<GeoJSON.Feature>();

  try {
    const result = await searchSingleLayerForConflicts(
      searchGeom,
      "Matriklen2",
      layer,
    );
    for (const f of filterFeaturesIntersectingGeometry(result?.features ?? [], geometry)) {
      seen.add(f);
    }
  } catch (e) {
    console.warn(`WFS intersect failed for ${layer}`, e);
  }

  if (seen.size === 0) {
    try {
      const bounds = bbox(feature(geometry)) as [number, number, number, number];
      const result = await DataCollector.WFS.getFeatureBBOX(
        "Matriklen2",
        layer as typeof MATRIKEL_CONFLICT_LAYER,
        bounds,
      );
      for (const f of filterFeaturesIntersectingGeometry(result?.features ?? [], geometry)) {
        seen.add(f);
      }
    } catch (e) {
      console.warn(`WFS bbox failed for ${layer}`, e);
    }
  }

  return [...seen];
}

function extractJordstykkeOverlapInfo(
  props: Record<string, unknown> | null | undefined,
): JordstykkeOverlapInfo | undefined {
  if (!props) {
    return undefined;
  }
  const matnrRaw = props.matrikelnummer ?? props.Matrikelnummer;
  const matnr =
    matnrRaw === undefined || matnrRaw === null ? "" : String(matnrRaw).trim();
  if (!matnr) {
    return undefined;
  }
  const ejerlavRaw = props.ejerlavsnavn ?? props.Ejerlavsnavn;
  const ejerlav =
    ejerlavRaw === undefined || ejerlavRaw === null
      ? ""
      : String(ejerlavRaw).trim();
  const ejerlavskodeRaw = props.ejerlavskode ?? props.Ejerlavskode;
  const ejerlavskode =
    ejerlavskodeRaw === undefined || ejerlavskodeRaw === null
      ? ""
      : String(ejerlavskodeRaw).trim();
  const bfeRaw =
    props.samletFastEjendomBFEnummer ??
    props.BFEnummer ??
    props.samletFastEjendomLokalId ??
    props.samletfastejendomlokalid;
  const bfe =
    bfeRaw === undefined || bfeRaw === null ? "" : String(bfeRaw).trim();
  const identityRaw =
    props.id_lokalId ??
    props.lokalId ??
    `${matnr}|${ejerlavskode || ejerlav}`;
  return {
    identity: String(identityRaw).trim(),
    matnr,
    ejerlav,
    ejerlavskode,
    bfe,
  };
}

function formatJordstykkeOverlapInfo(info: JordstykkeOverlapInfo): string {
  const inkludeEjerlavskode = info.ejerlavskode ? ` (${info.ejerlavskode})` : "";
  return `${info.matnr} ${info.ejerlav}${inkludeEjerlavskode}`.trim();
}

/** Konfliktsøgning: jordstykker whose geometry overlaps the drawn area. */
async function resolveOverlappingJordstykker(
  geometry: { type?: string; coordinates?: unknown },
): Promise<JordstykkeOverlapInfo[]> {
  const typedGeometry = asPolygonGeometry(geometry);
  if (!typedGeometry) {
    return [];
  }

  const features = await searchOverlappingWfsFeatures(
    typedGeometry,
    MATRIKEL_CONFLICT_LAYER,
  );
  const seen = new Set<string>();
  const overlapping: JordstykkeOverlapInfo[] = [];
  for (const f of features) {
    const info = extractJordstykkeOverlapInfo(
      f.properties as Record<string, unknown> | undefined,
    );
    if (!info || seen.has(info.identity)) {
      continue;
    }
    seen.add(info.identity);
    overlapping.push(info);
  }
  overlapping.sort((a, b) => a.matnr.localeCompare(b.matnr, "da"));
  return overlapping;
}

async function resolveArbitraryPolygonBfeAndMatr(
  polygonGeoJson: { type?: string; coordinates?: unknown },
  lookupPoint: LngLat,
): Promise<{ subjectBfe?: string; subjectMatr?: string }> {
  const overlapping = await resolveOverlappingJordstykker(polygonGeoJson);

  if (overlapping.length === 1) {
    const jordstykke = overlapping[0];
    return {
      subjectBfe: jordstykke.bfe || undefined,
      subjectMatr: formatJordstykkeOverlapInfo(jordstykke),
    };
  }

  if (overlapping.length > 1) {
    const first = overlapping[0];
    const distinctBfes = new Set(
      overlapping.map((j) => j.bfe).filter(Boolean),
    );
    return {
      subjectBfe:
        distinctBfes.size > 1
          ? appendMultiValueSuffix(first.bfe)
          : first.bfe || undefined,
      subjectMatr: appendMultiValueSuffix(formatJordstykkeOverlapInfo(first)),
    };
  }

  return resolveBfeAndMatrAtPoint(lookupPoint.lat, lookupPoint.lng);
}

function appendMultiValueSuffix(value: string | undefined): string | undefined {
  if (!value) {
    return value;
  }
  const suffix = ` ${MULTI_VALUE_PLACEHOLDER}`;
  if (value.endsWith(suffix) || value.endsWith(MULTI_VALUE_PLACEHOLDER)) {
    return value;
  }
  return `${value}${suffix}`;
}

/** Guaranteed interior point for Polygon / MultiPolygon (largest ring for multipolygons). */
export function interiorPointInGeometry(
  geometry: { type?: string; coordinates?: unknown } | null | undefined,
): LngLat | null {
  if (!geometry?.type || !geometry.coordinates) {
    return null;
  }
  if (geometry.type === "Polygon") {
    const coords = polylabel(geometry.coordinates as Parameters<typeof polylabel>[0], 0.00001);
    return { lng: coords[0], lat: coords[1] };
  }
  if (geometry.type === "MultiPolygon") {
    const polygons = geometry.coordinates as number[][][][];
    let best: LngLat | null = null;
    let bestArea = -1;
    for (const polyCoords of polygons) {
      const poly = polygon(polyCoords as number[][][]);
      const polyArea = area(poly);
      if (polyArea > bestArea) {
        const coords = polylabel(polyCoords as Parameters<typeof polylabel>[0], 0.00001);
        best = { lng: coords[0], lat: coords[1] };
        bestArea = polyArea;
      }
    }
    return best;
  }
  return null;
}

async function resolveSfeLokalIdFromLngLat(lng: number, lat: number): Promise<number | null> {
  try {
    const utm = GenerateGeom.convertFromLatLngToUTM({ lat, lng });
    const res = await PropertyInfoInterface.getMatrikelData(utm);
    const raw = res?.features?.[0]?.properties?.samletfastejendomlokalid;
    if (raw !== undefined && raw !== null && raw !== "") {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
  } catch {
    /* try WFS SFE at point */
  }
  try {
    const props = await PropertyInfoInterface.getSFEFromPoint({ lat, lng });
    const raw =
      props?.BFEnummer ??
      props?.samletFastEjendomLokalId ??
      props?.samletfastejendomlokalid;
    if (raw !== undefined && raw !== null && raw !== "") {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
  } catch {
    /* no SFE */
  }
  return null;
}

async function resolveEbrAddressForBfe(bfe: string): Promise<string | undefined> {
  try {
    const rows = await DataCollector.DF_GQL.EBR.EBRAdresseOgPunkt([bfe]);
    const line = rows[0]?.betegnelse?.trim();
    return line || undefined;
  } catch (e) {
    console.warn("Failed to resolve EBR address for BFE", bfe, e);
    return undefined;
  }
}

export function formatSubjectMatrFromSfeFeatures(features: any[]): string | undefined {
  const ejendomsInfo = features
    .map((f) => ({
      matnr:
        typeof f?.properties?.Matrikelnummer === "string"
          ? f.properties.Matrikelnummer.trim()
          : "",
      ejerlav:
        typeof f?.properties?.Ejerlavsnavn === "string"
          ? f.properties.Ejerlavsnavn.trim()
          : "",
      ejerlavskode:
        f?.properties?.Ejerlavskode != null
          ? String(f.properties.Ejerlavskode).trim()
          : "",
    }))
    .filter((e) => e.matnr);
  if (ejendomsInfo.length === 0) {
    return undefined;
  }
  const matNrCount = ejendomsInfo.length;
  const ejerlavCount = new Set(ejendomsInfo.map((a) => a.ejerlav).filter(Boolean)).size;
  const inkludeEjerlavskode = ejendomsInfo[0].ejerlavskode
    ? ` (${ejendomsInfo[0].ejerlavskode})`
    : "";
  if (ejerlavCount > 1) {
    return `${ejendomsInfo[0].matnr} ${ejendomsInfo[1].ejerlav}${inkludeEjerlavskode} m.fl`;
  }
  if (matNrCount > 1) {
    return `${ejendomsInfo[0].matnr} m.fl ${ejendomsInfo[0].ejerlav}${inkludeEjerlavskode}`;
  }
  return `${ejendomsInfo[0].matnr} ${ejendomsInfo[0].ejerlav}${inkludeEjerlavskode}`;
}

async function resolveKommuneAtPoint(lat: number, lng: number): Promise<string | undefined> {
  try {
    const resp = await AddressInterface.reverseGeocodeFull(lat, lng);
    if (!resp.ok) {
      return undefined;
    }
    const json = await resp.json();
    const kommuneNavn = json?.kommune?.navn;
    if (typeof kommuneNavn === "string" && kommuneNavn.trim()) {
      return kommuneNavn.trim();
    }
  } catch (e) {
    console.warn("Failed to resolve kommune from lookup point", e);
  }
  return undefined;
}

async function resolveBfeAndMatrAtPoint(
  lat: number,
  lng: number,
): Promise<{ subjectBfe?: string; subjectMatr?: string }> {
  try {
    const utm = GenerateGeom.convertFromLatLngToUTM({ lat, lng });
    const matrikelData = await PropertyInfoInterface.getMatrikelData(utm);
    const props = matrikelData?.features?.[0]?.properties ?? {};
    const sfeRaw =
      props.samletfastejendomlokalid ??
      props.SFEnummer ??
      props.bfe ??
      props.BFEnr ??
      props.bfenr;
    if (sfeRaw === undefined || sfeRaw === null || sfeRaw === "") {
      return {};
    }
    const subjectBfe = String(sfeRaw);
    let subjectMatr: string | undefined;
    try {
      const sfeFc = await PropertyInfoInterface.fetchMat2CadasterWFSForSFE(subjectBfe);
      subjectMatr = formatSubjectMatrFromSfeFeatures(sfeFc?.features ?? []);
    } catch (e) {
      console.warn("Failed to fetch matrikler for SFE", subjectBfe, e);
    }
    if (!subjectMatr) {
      const matrikelNummer = props.matrikelnummer ?? props.matrikelnr ?? "";
      const ejerlavsnavn = props.ejerlavsnavn ?? "";
      const txt = [matrikelNummer, ejerlavsnavn].filter(Boolean).join(", ");
      subjectMatr = txt || undefined;
    }
    return { subjectBfe, subjectMatr };
  } catch (e) {
    console.warn("Failed to resolve subject ejendom info from lookup point", e);
    return {};
  }
}

async function resolveMatrForKnownBfe(subjectBfe: string): Promise<string | undefined> {
  try {
    const sfeFc = await PropertyInfoInterface.fetchMat2CadasterWFSForSFE(subjectBfe);
    return formatSubjectMatrFromSfeFeatures(sfeFc?.features ?? []);
  } catch (e) {
    console.warn("Failed to fetch matrikler for known SFE", subjectBfe, e);
    return undefined;
  }
}

export async function resolveJpbReportSubjectInfo(options: {
  /** Layer chosen for the search buffer. */
  selectedLayer: LayerInfo | undefined;
  /** Layer selected in polygon analyzer — preferred for arbitrary vs SFE detection. */
  analyzerLayer?: LayerInfo | undefined;
  /** Polygon-analyzer outline geometry; authoritative source for subject ejendom metadata. */
  polygonGeoJson: { type?: string; coordinates?: unknown } | undefined;
  selectedLayerName: string | undefined;
  resolveAddressWithRetry: (lat: number, lng: number) => Promise<string | null>;
}): Promise<JpbSubjectInfoResult> {
  const {
    selectedLayer,
    analyzerLayer,
    polygonGeoJson,
    selectedLayerName,
    resolveAddressWithRetry,
  } = options;
  const layerForAnalysisType = analyzerLayer ?? selectedLayer;

  const result: JpbSubjectInfoResult = {};
  let lookupPoint: LngLat | null = null;
  let knownBfe: string | undefined;

  if (polygonGeoJson) {
    // Always derive metadata from the polygon-analyzer subject area — never from the
    // search-buffer pin that may sit on a building address elsewhere on the SFE.
    lookupPoint = interiorPointInGeometry(polygonGeoJson);

    if (isArbitraryPolygonAnalysis(layerForAnalysisType)) {
      if (lookupPoint) {
        result.subjectAddress =
          (await resolveAddressWithRetry(lookupPoint.lat, lookupPoint.lng)) ?? undefined;

        const atPolygon = await resolveArbitraryPolygonBfeAndMatr(
          polygonGeoJson,
          lookupPoint,
        );
        result.subjectBfe = atPolygon.subjectBfe;
        result.subjectMatr = atPolygon.subjectMatr;
      }
    } else {
      if (isBfePropertyPolygonLayer(layerForAnalysisType)) {
        knownBfe = extractBfeFromLayer(layerForAnalysisType);
      } else if (isBfePropertyPolygonLayer(selectedLayer)) {
        knownBfe = extractBfeFromLayer(selectedLayer);
      }
      if (!knownBfe && lookupPoint) {
        const sfeId = await resolveSfeLokalIdFromLngLat(lookupPoint.lng, lookupPoint.lat);
        if (sfeId !== null) {
          knownBfe = String(sfeId);
        }
      }

      if (knownBfe) {
        result.subjectAddress = await resolveEbrAddressForBfe(knownBfe);
      }
      if (!result.subjectAddress && lookupPoint) {
        result.subjectAddress =
          (await resolveAddressWithRetry(lookupPoint.lat, lookupPoint.lng)) ?? undefined;
      }

      if (knownBfe) {
        result.subjectBfe = knownBfe;
        result.subjectMatr = await resolveMatrForKnownBfe(knownBfe);
      } else if (lookupPoint) {
        const atPoint = await resolveBfeAndMatrAtPoint(lookupPoint.lat, lookupPoint.lng);
        result.subjectBfe = atPoint.subjectBfe;
        result.subjectMatr = atPoint.subjectMatr;
      }
    }
  } else if (selectedLayer) {
    // Fallback when report is generated without a polygon-analyzer outline.
    const isPointLayer = MapitUtils.isPointLayer(selectedLayer.type);
    if (isPointLayer) {
      const pins = collectLngLatsFromPointLayer(selectedLayer);
      if (pins.length > 0) {
        lookupPoint = { lng: pins[0][0], lat: pins[0][1] };
      }
    } else {
      const firstPolygon = selectedLayer.geoJson?.features?.find(
        (f) =>
          f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon",
      );
      lookupPoint = interiorPointInGeometry(firstPolygon?.geometry);
    }

    knownBfe = extractBfeFromLayer(selectedLayer);
    if (!knownBfe && lookupPoint) {
      const sfeId = await resolveSfeLokalIdFromLngLat(lookupPoint.lng, lookupPoint.lat);
      if (sfeId !== null) {
        knownBfe = String(sfeId);
      }
    }

    if (knownBfe && isBfePropertyPolygonLayer(selectedLayer)) {
      result.subjectAddress = await resolveEbrAddressForBfe(knownBfe);
    }
    if (!result.subjectAddress && lookupPoint) {
      result.subjectAddress =
        (await resolveAddressWithRetry(lookupPoint.lat, lookupPoint.lng)) ?? undefined;
    }

    if (knownBfe) {
      result.subjectBfe = knownBfe;
      result.subjectMatr = await resolveMatrForKnownBfe(knownBfe);
    } else if (lookupPoint) {
      const atPoint = await resolveBfeAndMatrAtPoint(lookupPoint.lat, lookupPoint.lng);
      result.subjectBfe = atPoint.subjectBfe;
      result.subjectMatr = atPoint.subjectMatr;
    }
  }

  if (!result.subjectAddress) {
    result.subjectAddress = selectedLayerName;
  }

  if (lookupPoint) {
    result.subjectKommune = await resolveKommuneAtPoint(lookupPoint.lat, lookupPoint.lng);
  }

  return result;
}
